import { useState } from 'react';
import { NonEmptyArray } from '../../../utils/types';
import { entries, isDefined } from '../../../utils/utils';

export type FormControlError = {
  message: string;
};

type FormValidation<F, K extends keyof F, V extends F[K]> = (
  form: F,
  key: K,
  v: V | undefined | null
) => FormControlError | undefined;
type FormArrayValidation<F, K extends keyof F, V extends F[K]> = (
  form: F,
  key: K,
  v: V,
  index: number
) => FormControlError | undefined;

export type FormValidations<F> = {
  [K in keyof F]?: F[K] extends
    | string
    | number
    | boolean
    | Date
    | File
    | undefined
    | null
    ? FormValidation<F, K, F[K]>[]
    : F[K] extends Array<unknown>
    ? FormArrayValidation<F, K, F[K]>
    : FormValidations<F[K]>;
};

type FromGroupError<F> = FormErrors<F>;

type FormErrors<F> = {
  [K in keyof F]: F[K] extends
    | string
    | number
    | boolean
    | Date
    | File
    | undefined
    | null
    ? NonEmptyArray<FormControlError>
    : F[K] extends Array<unknown>
    ? NonEmptyArray<FormControlError>
    : FromGroupError<F[K]>;
};

function getInitFormErrors<F>(form: F): FormErrors<F> {
  return entries(form).reduce((acc, [key, value]) => {
    if (typeof value === 'object') {
      return {
        ...acc,
        [key]: getInitFormErrors(value),
      };
    } else {
      return {
        ...acc,
        [key]: undefined,
      };
    }
  }, {} as FormErrors<F>);
}

function getFormErrors<F>(
  validatedOn: FormValidationOn,
  formState: FormState,
  initForm: F,
  form: F,
  formErrors: FormErrors<F>,
  formValidations: FormValidations<F>
): {
  errors: FormErrors<F>;
  isErrors: boolean;
} {
  let isErrors = false;
  const errors = entries(formValidations).reduce((acc, [key, value]) => {
    if (typeof value === 'undefined') {
      return {
        ...acc,
        [key]: undefined,
      };
    }
    if (typeof value === 'function') {
      const values = form[key] as unknown as Array<unknown>;
      if (
        (validatedOn === 'onSubmit' && formState === 'submitted') ||
        validatedOn === 'onUpdate'
      ) {
        return {
          ...acc,
          [key]: values.map((v, i) => {
            const error = value(form, key, form[key], i);
            if (error) {
              isErrors = true;
            }
            return error;
          }),
        };
      } else {
        const newErrors: FormControlError[] | undefined = formErrors[
          key
        ] as unknown as NonEmptyArray<FormControlError>;
        return {
          ...acc,
          [key]: newErrors,
        };
      }
    }

    if (Array.isArray(value)) {
      const isTouched = form[key] !== initForm[key] || formErrors[key];
      let newErrors: FormControlError[] | undefined = formErrors[
        key
      ] as unknown as NonEmptyArray<FormControlError>;
      if (validatedOn === 'onSubmit' && formState === 'submitted') {
        newErrors = value
          .map((validation) => validation(form, key, form[key]))
          .filter(isDefined);
      } else if (validatedOn === 'onUpdate' && isTouched) {
        newErrors = value
          .map((validation) => validation(form, key, form[key]))
          .filter(isDefined);
      }
      if (newErrors?.length) {
        isErrors = true;
      }
      return {
        ...acc,
        [key]: newErrors,
      };
    }
    const { errors: newErrors, isErrors: elementIsError } = getFormErrors(
      validatedOn,
      formState,
      initForm[key],
      form[key],
      formErrors[key] as FormErrors<F[typeof key]>,
      value as FormValidations<F[typeof key]>
    );
    if (elementIsError) {
      isErrors = true;
    }
    return {
      ...acc,
      [key]: newErrors,
    };
  }, {} as FormErrors<F>);
  return { errors, isErrors };
}

type FormState = 'init' | 'updated' | 'submitted';
type FormValidationOn = 'onUpdate' | 'onSubmit';

export const useForm = <F>(
  validateOn: FormValidationOn,
  initForm: F,
  formValidations: FormValidations<F>
) => {
  const [form, setForm] = useState<F>(initForm);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [errors, setErrors] = useState<FormErrors<F>>(getInitFormErrors(form));
  const [formState, setFormState] = useState<FormState>('init');

  const validate = (f: F) => {
    setForm(f);
    let newFormState = formState;
    if (formState === 'init') {
      newFormState = 'updated';
      setFormState(newFormState);
    }
    const { errors: newErrors, isErrors } = getFormErrors(
      validateOn,
      newFormState,
      initForm,
      f,
      errors,
      formValidations
    );
    setErrors(newErrors);
    setIsValid(!isErrors);
  };

  const submit = (): boolean => {
    setForm(form);
    setFormState('submitted');
    const { errors: newErrors, isErrors } = getFormErrors(
      validateOn,
      'submitted',
      initForm,
      form,
      errors,
      formValidations
    );
    setErrors(newErrors);
    setIsValid(!isErrors);
    return !isErrors;
  };

  const reset = () => {
    setErrors(getInitFormErrors(initForm));
    setFormState('init');
  };

  return {
    form,
    errors,
    formState,
    isValid,
    validate,
    submit,
    reset,
  };
};
